import { head, takeLast } from "ramda";
import { getPrettyUrl } from "get_base_uri";

import trackAnalytic from "helpers/analytics";
import {
  findCardTypeByFirstDigit,
  findFormattersByLowercase,
  formatObscuredCardNumberByLowercase,
} from "helpers/credit_cards";
import isOLO from "helpers/is_olo";
import {
  newCardValidators,
  validateBlurredField,
  validateCardDetails,
} from "helpers/validation/forms";
import VALIDATION_MESSAGES from "helpers/validation/messages";
import Validators from "helpers/validation/validators";
import {
  deleteCreditCard,
  postCreditCard,
  postTokenRequestForNewCard,
  putCreditCard,
} from "interfaces/credit_cards";
import { registerCard } from "interfaces/order";

export const CANCEL_CREDIT_CARD_UPDATES =
  "credit_cards/CANCEL_CREDIT_CARD_UPDATES";
export const CANCEL_NEW_CARD_RECORD = "credit_cards/CANCEL_NEW_CARD_RECORD";
export const CREATE_NEW_LOCAL_CARD = "credit_cards/CREATE_NEW_LOCAL_CARD";
export const DELETE_LOCAL_CARD_RECORD = "credit_cards/DELETE_LOCAL_CARD_RECORD";
export const RECEIVE_CREDIT_CARD = "credit_cards/RECEIVE_CREDIT_CARD";
export const RETURN_ERRORS = "credit_cards/RETURN_ERRORS";
export const TOGGLE_FORM = "credit_cards/TOGGLE_FORM";
export const UPDATE_CARD_CVV = "credit_cards/UPDATE_CARD_CVV";
export const UPDATE_CREDIT_CARD_FIELD = "credit_cards/UPDATE_CREDIT_CARD_FIELD";
export const VALIDATE_CC_FIELD_ON_BLUR =
  "credit_cards/VALIDATE_CC_FIELD_ON_BLUR";
export const SET_CREDIT_CARD_ERROR_MESSAGES =
  "credit_cards/SET_CREDIT_CARD_ERROR_MESSAGES";

const loyaltyCardValidators = {
  cardNumber: Validators.isValidCardNumber,
};

function validateCCFieldOnBlur(field, card) {
  const error = validateBlurredField(
    field,
    card,
    newCardValidators,
    VALIDATION_MESSAGES
  );

  return {
    type: VALIDATE_CC_FIELD_ON_BLUR,
    error,
    field,
    card,
  };
}

function updateCreditCardField(cardUuid, field, input) {
  const { value } = input.target;
  return {
    type: UPDATE_CREDIT_CARD_FIELD,
    cardUuid,
    field,
    value,
  };
}

function updateCardCvv(cardUuid, cvv) {
  return {
    type: UPDATE_CARD_CVV,
    cardUuid,
    cvv,
  };
}

function createNewLocalCard() {
  return {
    type: CREATE_NEW_LOCAL_CARD,
  };
}

function receiveCreditCard(card) {
  return {
    type: RECEIVE_CREDIT_CARD,
    card,
  };
}

function cancelCreditCardUpdates(cardUuid) {
  return {
    type: CANCEL_CREDIT_CARD_UPDATES,
    cardUuid,
  };
}

function deleteLocalCardRecord(cardUuid) {
  return {
    type: DELETE_LOCAL_CARD_RECORD,
    cardUuid,
  };
}

function cancelNewCardRecord() {
  return {
    type: CANCEL_NEW_CARD_RECORD,
  };
}

function returnCardErrors(card, errors) {
  return {
    type: RETURN_ERRORS,
    cardUuid: card.cardUuid,
    errors,
  };
}

function saveCreditCardUpdates(card) {
  const errors = validateCardDetails(card, loyaltyCardValidators);

  if (errors) {
    return {
      type: RETURN_ERRORS,
      cardUuid: card.cardUuid,
      errors,
    };
  }
  return dispatch => {
    putCreditCard(card, response => {
      dispatch(receiveCreditCard(response));
    });
  };
}

function saveNewCreditCardWithToken(card, tokenResponse) {
  if (isOLO()) {
    const prettyUrl = getPrettyUrl();
    const { firstName, lastName, cardNumber } = card;

    const cardInfo = {
      firstName,
      lastName,
      paymentMethodToken: tokenResponse,
      lastFour: takeLast(4, cardNumber),
      nameOnCard: `${firstName} ${lastName}`,
      cardType: findCardTypeByFirstDigit(
        head(cardNumber)
      ).prettyName.toLowerCase(),
      storePrettyUrl: prettyUrl,
    };

    return dispatch => {
      registerCard(cardInfo).then(({ cardId }) => {
        const newLocalCard = {
          cardUuid: cardId,
          lastFour: cardInfo.lastFour,
          lowercaseType: cardInfo.cardType,
          sentenceCaseType: findFormattersByLowercase(cardInfo.cardType)
            .prettyName,
          obscuredNumber: formatObscuredCardNumberByLowercase(
            cardInfo.cardType,
            cardInfo.lastFour
          ),
          dropdownFormat: `${
            findFormattersByLowercase(cardInfo.cardType).prettyName
          } ending in *** ${card.lastFour}`,
        };

        dispatch(receiveCreditCard(newLocalCard));
      });
    };
  }
  // BE is currently ONLY expecting the unencryptedPan and token
  // will expand to include further details in next version

  const cardWithToken = {
    cardEnrollmentToken: tokenResponse.token,
    unencryptedPan: card.cardNumber,
  };

  return dispatch => {
    postCreditCard(cardWithToken, response => {
      dispatch(receiveCreditCard(response));
    }).catch(errorResp => {
      // 409 CONFLICT
      console.log(errorResp);
      dispatch(
        returnCardErrors(card, {
          cardNumber:
            "Sorry, that card is already enrolled. Did you add it to a different account?",
        })
      );
    });
  };
}

function saveNewCreditCard(card) {
  // LOYALTY SAVE ONLY, OLO USES IFRAME
  const errors = validateCardDetails(card, loyaltyCardValidators);

  if (errors) {
    return {
      type: RETURN_ERRORS,
      cardUuid: card.cardUuid,
      errors,
    };
  }

  trackAnalytic("Account Page: Add CC");

  return dispatch => {
    // Only stored data for loyalty cards at the moment

    postTokenRequestForNewCard(response => {
      dispatch(saveNewCreditCardWithToken(card, response));
    });
  };
}

function removeCreditCard(card) {
  return dispatch => {
    deleteCreditCard(card, () => {
      // On Success: We receive []
      dispatch(deleteLocalCardRecord(card.cardUuid));
    });
  };
}

function toggleForm(cardUuid) {
  return {
    type: TOGGLE_FORM,
    cardUuid,
  };
}

export function setCreditCardValidationMessages(error) {
  return {
    type: SET_CREDIT_CARD_ERROR_MESSAGES,
    error,
  };
}

export {
  cancelCreditCardUpdates,
  cancelNewCardRecord,
  createNewLocalCard,
  removeCreditCard,
  returnCardErrors,
  saveCreditCardUpdates,
  saveNewCreditCard,
  saveNewCreditCardWithToken,
  toggleForm,
  updateCreditCardField,
  updateCardCvv,
  validateCCFieldOnBlur,
};
